<template>
  <div class="legal-page">
    <div class="legal-container">
      <!-- Header -->
      <header class="legal-header">
        <div class="header-content">
          <router-link to="/" class="back-link">
            <a-icon type="arrow-left" /> Back to Home
          </router-link>
          <h1>Terms of Service</h1>
          <p class="last-updated">Last Updated: January 11, 2026</p>
        </div>
      </header>

      <!-- Content -->
      <div class="legal-content">
        <section>
          <h2>1. Agreement to Terms</h2>
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between you and
            ZeroCents ("Company," "we," "us," or "our") regarding your use of our financial tracking
            application and services (collectively, the "Service").
          </p>
          <p>
            By accessing or using our Service, you agree to be bound by these Terms. If you do not
            agree to these Terms, please do not use our Service.
          </p>
        </section>

        <section>
          <h2>2. Use of Service</h2>

          <h3>2.1 Eligibility</h3>
          <p>
            You must be at least 13 years old to use our Service. By using our Service, you represent
            and warrant that you meet this age requirement and have the legal capacity to enter into
            these Terms.
          </p>

          <h3>2.2 Account Registration</h3>
          <p>To use our Service, you must:</p>
          <ul>
            <li>Create an account with accurate and complete information</li>
            <li>Maintain the security of your password</li>
            <li>Promptly update your account information if it changes</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized access</li>
          </ul>

          <h3>2.3 Acceptable Use</h3>
          <p>You agree to use the Service only for lawful purposes. You must not:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Transmit malicious code or viruses</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with the proper functioning of the Service</li>
            <li>Use the Service to harm, threaten, or harass others</li>
            <li>Scrape, mine, or harvest data from the Service</li>
            <li>Use automated systems to access the Service without permission</li>
          </ul>
        </section>

        <section>
          <h2>3. Service Description</h2>

          <h3>3.1 Features</h3>
          <p>ZeroCents provides a financial tracking platform that allows you to:</p>
          <ul>
            <li>Create and manage multiple organizations</li>
            <li>Track financial transactions</li>
            <li>Categorize income and expenses</li>
            <li>Generate financial reports</li>
            <li>Upload and store receipts</li>
            <li>Collaborate with team members</li>
            <li>Monitor account balances</li>
          </ul>

          <h3>3.2 Free Service</h3>
          <p>
            Our Service is currently provided free of charge. We reserve the right to introduce
            paid features or subscription plans in the future with advance notice.
          </p>

          <h3>3.3 Service Modifications</h3>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the Service at any
            time, with or without notice. We are not liable for any modification, suspension, or
            discontinuation of the Service.
          </p>
        </section>

        <section>
          <h2>4. User Content and Data</h2>

          <h3>4.1 Your Data Ownership</h3>
          <p>
            You retain all rights to the financial data and content you submit to the Service
            ("User Content"). By submitting User Content, you grant us a limited license to use,
            store, and process your data solely to provide and improve the Service.
          </p>

          <h3>4.2 Data Accuracy</h3>
          <p>
            You are responsible for the accuracy and legality of your User Content. We are not
            responsible for any errors in your financial data or any consequences resulting from
            inaccurate data.
          </p>

          <h3>4.3 Data Backup</h3>
          <p>
            While we implement backup systems, you are responsible for maintaining your own backups
            of important financial data. We are not liable for any loss of data.
          </p>

          <h3>4.4 Data Export and Deletion</h3>
          <p>
            You may export your data at any time through the Service. Upon account deletion, we
            will delete your data in accordance with our Privacy Policy, subject to legal retention
            requirements.
          </p>
        </section>

        <section>
          <h2>5. Organizations and Multi-User Access</h2>

          <h3>5.1 Organization Creation</h3>
          <p>
            You may create organizations and invite other users to join. As an organization admin,
            you are responsible for managing member access and permissions.
          </p>

          <h3>5.2 Roles and Permissions</h3>
          <p>Organizations support three roles:</p>
          <ul>
            <li><strong>Admin:</strong> Full access to manage organization, members, and all data</li>
            <li><strong>Member:</strong> Can view and edit transactions and categories</li>
            <li><strong>Viewer:</strong> Read-only access to organization data</li>
          </ul>

          <h3>5.3 Organization Responsibility</h3>
          <p>
            Organization admins are responsible for ensuring that all members comply with these
            Terms. Admins should only grant access to trusted individuals.
          </p>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>

          <h3>6.1 Our Rights</h3>
          <p>
            The Service, including its design, features, code, and content (excluding User Content),
            is owned by ZeroCents and protected by copyright, trademark, and other intellectual
            property laws.
          </p>

          <h3>6.2 Limited License</h3>
          <p>
            We grant you a limited, non-exclusive, non-transferable, revocable license to access
            and use the Service for your personal or business financial tracking purposes.
          </p>

          <h3>6.3 Restrictions</h3>
          <p>You may not:</p>
          <ul>
            <li>Copy, modify, or create derivative works of the Service</li>
            <li>Reverse engineer or decompile any part of the Service</li>
            <li>Remove or modify any proprietary notices</li>
            <li>Use our trademarks without permission</li>
            <li>Frame or mirror any part of the Service</li>
          </ul>
        </section>

        <section>
          <h2>7. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>We do not warrant that:</p>
          <ul>
            <li>The Service will be uninterrupted, secure, or error-free</li>
            <li>The results obtained from the Service will be accurate or reliable</li>
            <li>Any errors in the Service will be corrected</li>
            <li>The Service will meet your specific requirements</li>
          </ul>
          <p>
            You acknowledge that you use the Service at your own risk and that we are not
            responsible for any financial decisions you make based on the Service.
          </p>
        </section>

        <section>
          <h2>8. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZEROCENTS SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
            DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN
            ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12)
            MONTHS PRECEDING THE CLAIM. SINCE THE SERVICE IS CURRENTLY FREE, THIS LIABILITY IS LIMITED
            TO $100 USD.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties or limitations on
            liability. In such jurisdictions, our liability will be limited to the maximum extent
            permitted by law.
          </p>
        </section>

        <section>
          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless ZeroCents, its officers, directors,
            employees, and agents from any claims, losses, damages, liabilities, costs, and expenses
            (including reasonable attorneys' fees) arising out of or related to:
          </p>
          <ul>
            <li>Your use of the Service</li>
            <li>Your User Content</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Your violation of any applicable laws or regulations</li>
          </ul>
        </section>

        <section>
          <h2>10. Privacy and Data Protection</h2>
          <p>
            Your use of the Service is also governed by our Privacy Policy, which is incorporated
            into these Terms by reference. Please review our
            <router-link to="/privacy">Privacy Policy</router-link>
            to understand how we collect, use, and protect your information.
          </p>
        </section>

        <section>
          <h2>11. Termination</h2>

          <h3>11.1 Your Right to Terminate</h3>
          <p>
            You may terminate your account at any time by contacting us or using the account
            deletion feature in your settings.
          </p>

          <h3>11.2 Our Right to Terminate</h3>
          <p>
            We reserve the right to suspend or terminate your account if:
          </p>
          <ul>
            <li>You violate these Terms</li>
            <li>Your use of the Service poses a security risk</li>
            <li>You engage in fraudulent or illegal activity</li>
            <li>We are required to do so by law</li>
            <li>We decide to discontinue the Service</li>
          </ul>

          <h3>11.3 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the Service will immediately cease. We will delete
            your data in accordance with our Privacy Policy, subject to legal retention requirements.
          </p>
        </section>

        <section>
          <h2>12. Dispute Resolution</h2>

          <h3>12.1 Informal Resolution</h3>
          <p>
            If you have any concerns or disputes, please contact us first at support@zerocents.com
            so we can attempt to resolve the issue informally.
          </p>

          <h3>12.2 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State
            of California, United States, without regard to its conflict of law provisions.
          </p>

          <h3>12.3 Jurisdiction</h3>
          <p>
            Any legal action or proceeding arising under these Terms will be brought exclusively in
            the federal or state courts located in San Francisco County, California, and you consent
            to personal jurisdiction in these courts.
          </p>
        </section>

        <section>
          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of material
            changes by posting the updated Terms on our website and updating the "Last Updated" date.
            Your continued use of the Service after changes become effective constitutes acceptance
            of the modified Terms.
          </p>
          <p>
            If you do not agree to the modified Terms, you must stop using the Service and may
            terminate your account.
          </p>
        </section>

        <section>
          <h2>14. General Provisions</h2>

          <h3>14.1 Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between
            you and ZeroCents regarding the Service.
          </p>

          <h3>14.2 Severability</h3>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, the remaining
            provisions will continue in full force and effect.
          </p>

          <h3>14.3 Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms will not be considered a
            waiver of those rights.
          </p>

          <h3>14.4 Assignment</h3>
          <p>
            You may not assign or transfer these Terms or your rights under them without our written
            consent. We may assign these Terms without restriction.
          </p>

          <h3>14.5 Third-Party Rights</h3>
          <p>
            These Terms do not create any third-party beneficiary rights.
          </p>

          <h3>14.6 Force Majeure</h3>
          <p>
            We are not liable for any failure or delay in performance due to circumstances beyond
            our reasonable control, including acts of God, natural disasters, war, terrorism, labor
            disputes, or internet service interruptions.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> support@zerocents.com</li>
            <li><strong>Address:</strong> 123 Finance Street, San Francisco, CA 94105</li>
          </ul>
        </section>

        <section>
          <h2>16. Acknowledgment</h2>
          <p>
            BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE,
            UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM.
          </p>
        </section>
      </div>

      <!-- Footer Navigation -->
      <div class="legal-footer">
        <router-link to="/" class="footer-link">Home</router-link>
        <router-link to="/privacy" class="footer-link">Privacy Policy</router-link>
        <a href="mailto:support@zerocents.com" class="footer-link">Contact Us</a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Terms',
  mounted() {
    window.scrollTo(0, 0);
  }
};
</script>

<style lang="scss" scoped>
.legal-page {
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem 0;
}

.legal-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}

.legal-header {
  background: #fff;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.header-content {
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1890ff;
    margin: 1rem 0 0.5rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  .last-updated {
    color: #666;
    font-style: italic;
    margin: 0;
  }
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #1890ff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;

  &:hover {
    color: #40a9ff;
  }
}

.legal-content {
  background: #fff;
  border-radius: 8px;
  padding: 3rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  section {
    margin-bottom: 2.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #1890ff;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #555;
    margin: 1.5rem 0 1rem;
  }

  p {
    line-height: 1.8;
    color: #555;
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 2rem;

    li {
      line-height: 1.8;
      color: #555;
      margin-bottom: 0.5rem;

      strong {
        color: #333;
      }
    }
  }

  a {
    color: #1890ff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.legal-footer {
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}

.footer-link {
  color: #1890ff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;

  &:hover {
    color: #40a9ff;
  }
}
</style>
